/**
 * 全局 Toast Hook（LEO 规范 §6.7 + §7 反馈原则）。
 * 单例实现：任意位置调用 useToast().push()，底部弹出，2.2s 自动消失。
 */

import { useCallback, useEffect, useState } from "react";

export type ToastTone = "info" | "success" | "error";

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

let nextId = 1;
let externalPush: ((msg: string, tone?: ToastTone) => void) | null = null;

export function pushToast(message: string, tone: ToastTone = "info"): void {
  if (externalPush) externalPush(message, tone);
}

export function useToast(): {
  toasts: ToastItem[];
  push: (msg: string, tone?: ToastTone) => void;
  dismiss: (id: number) => void;
} {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, tone: ToastTone = "info") => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, tone }]);
      setTimeout(() => {
        dismiss(id);
      }, 2200);
    },
    [dismiss],
  );

  useEffect(() => {
    externalPush = push;
    return () => {
      if (externalPush === push) externalPush = null;
    };
  }, [push]);

  return { toasts, push, dismiss };
}